import { FiTarget } from 'react-icons/fi';

export const predictionSkillsData = {
  courseId: 'prediction-skills',
  courseType: 'listening-course',
  title: 'Prediction Skills',
  icon: FiTarget,
  overview: {
    impact: 'Anticipate answers before listening to gain a critical advantage',
    focus: 'Using question text, grammar, and context to predict answer types',
    uses: 'All 4 listening sections',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Why Prediction Matters',
      content: `<p>Prediction is one of the most powerful IELTS Listening strategies. When you predict answers <strong>before</strong> the audio plays, you prime your brain to recognize the right information instantly. Without prediction, you're listening passively and hoping to catch answers — a much harder task.</p>
<p>During the preparation time before each section, skilled test-takers don't just read the questions — they actively predict what they'll hear. This means you already know <em>what kind of answer</em> to listen for, so you can focus your attention instead of trying to process everything at once.</p>`,
      points: [
        'Prediction turns passive listening into active, focused listening',
        'You use the 30-second preview time to analyse each question',
        'Even a rough prediction narrows down what you need to hear',
        'Strong predictors score 1-2 bands higher on average',
      ],
    },
    {
      title: 'Predicting Answer Types',
      content: `<p>Every gap or question in IELTS Listening expects a specific <strong>type</strong> of information. By analysing the question, you can predict whether the answer is a name, number, place, reason, date, or object. This table shows common gap patterns and the answer type you should expect:</p>`,
      table: {
        headers: ['Question / Gap Pattern', 'Expected Answer Type', 'Example Prediction'],
        rows: [
          ['The class starts at ___', 'Time', 'A clock time like "9:30" or "ten fifteen"'],
          ['Contact name: ___', 'Person\'s name', 'A first name or surname, likely spelled out'],
          ['Location: ___ Street', 'Street name', 'A proper noun, possibly spelled letter by letter'],
          ['Total cost: $___', 'Number / Price', 'A number, possibly with decimals'],
          ['Reason for complaint: ___', 'Reason / Description', 'A phrase explaining why, e.g. "too noisy"'],
          ['Number of participants: ___', 'Number', 'A whole number, listen for "twenty" vs "twelve"'],
          ['Date of event: ___', 'Date', 'A date like "March 15th" or "the 3rd of June"'],
          ['Main material: ___', 'Noun (object/material)', 'A material like "wood", "plastic", or "cotton"'],
        ],
      },
    },
    {
      title: 'Grammatical Prediction',
      content: `<p>Grammar is a powerful prediction tool. The words <strong>around</strong> the gap tell you exactly what word class fits. If you can predict whether the answer is a noun, adjective, verb, or adverb, you'll recognize it much faster when you hear it.</p>`,
      points: [
        '<strong>After "a" or "the":</strong> Expect a noun — "The ___ was cancelled" → noun like "meeting" or "flight"',
        '<strong>Before a noun:</strong> Expect an adjective — "a ___ building" → adjective like "modern" or "tall"',
        '<strong>After a subject:</strong> Expect a verb — "Students must ___" → verb like "register" or "attend"',
        '<strong>Modifying a verb:</strong> Expect an adverb — "She spoke ___" → adverb like "quickly" or "clearly"',
        '<strong>After a preposition:</strong> Expect a noun or gerund — "interested in ___" → "photography" or "swimming"',
      ],
      warning: 'Always check the word limit! If the limit is ONE WORD ONLY, your prediction must be a single word.',
    },
    {
      title: 'Context Prediction Techniques',
      content: `<p>Beyond individual gaps, the surrounding context — section headings, neighbouring questions, and the overall topic — provides strong clues about what to expect.</p>`,
      points: [
        '<strong>Use headings:</strong> If the heading says "Accommodation Options", answers will relate to housing — types, prices, locations',
        '<strong>Read surrounding questions:</strong> If Q1 asks about a person\'s name and Q2 about their phone number, you\'re in a form-filling section — expect personal details',
        '<strong>Identify the situation:</strong> Questions about "opening hours", "admission fee", and "nearest station" suggest a tourist information context',
        '<strong>Notice question flow:</strong> IELTS questions follow the audio order, so the sequence of questions maps to the sequence of the conversation or talk',
        '<strong>Anticipate keywords:</strong> If the question says "The main advantage of the new system is ___", listen for positive evaluative language: "benefit", "improvement", "better"',
      ],
    },
  ],
  exercises: [
    {
      title: 'Predict the Answer Type',
      type: 'select',
      instructions: 'For each gap, predict what type of information the answer will be.',
      questions: [
        {
          text: 'Membership expires on ___.',
          options: ['A date', 'A person\'s name', 'A reason'],
          answer: 'A date',
          explanation: '"Expires on" requires a date — e.g. "31st December" or "June 15th".',
        },
        {
          text: 'The hotel is located on ___ Road.',
          options: ['A street name', 'A number', 'A time'],
          answer: 'A street name',
          explanation: 'The word "Road" after the gap indicates a proper noun street name is expected.',
        },
        {
          text: 'Maximum group size: ___ people.',
          options: ['A number', 'A name', 'A description'],
          answer: 'A number',
          explanation: '"Maximum group size" with "people" requires a number like "15" or "twenty".',
        },
        {
          text: 'The speaker recommends visiting the ___ gallery.',
          options: ['A name or adjective', 'A time', 'A number'],
          answer: 'A name or adjective',
          explanation: 'Before "gallery", you expect a name (e.g. "National") or descriptive word (e.g. "modern").',
        },
      ],
    },
    {
      title: 'Identify Expected Word Class',
      type: 'select',
      instructions: 'Use grammar clues to determine what word class (part of speech) the answer should be.',
      questions: [
        {
          text: 'The project was ___ successful. (word before adjective)',
          options: ['Adverb', 'Noun', 'Verb'],
          answer: 'Adverb',
          explanation: 'A word modifying an adjective ("successful") must be an adverb — e.g. "extremely" or "highly".',
        },
        {
          text: 'Visitors must ___ at the front desk. (after "must")',
          options: ['Verb (base form)', 'Adjective', 'Noun'],
          answer: 'Verb (base form)',
          explanation: 'After a modal verb like "must", you need a base verb — e.g. "register" or "check in".',
        },
        {
          text: 'They built a ___ bridge across the river. (before a noun)',
          options: ['Adjective', 'Verb', 'Adverb'],
          answer: 'Adjective',
          explanation: 'A word placed between "a" and "bridge" must be an adjective — e.g. "wooden" or "narrow".',
        },
        {
          text: 'She is interested in ___. (after a preposition)',
          options: ['Noun or gerund', 'Adjective', 'Adverb'],
          answer: 'Noun or gerund',
          explanation: 'After a preposition like "in", expect a noun ("history") or gerund ("painting").',
        },
      ],
    },
    {
      title: 'Predict from Context',
      type: 'select',
      instructions: 'Use the surrounding context to predict the most likely answer.',
      questions: [
        {
          text: 'Section heading: "Library Facilities". Question: "The photocopier is on the ___ floor."',
          options: ['A floor number (e.g. second, third)', 'A person\'s name', 'A reason'],
          answer: 'A floor number (e.g. second, third)',
          explanation: 'The heading tells you this is about the library layout. "Floor" requires an ordinal number.',
        },
        {
          text: 'Previous question asked about "check-in time". Next question: "Check-out time: ___"',
          options: ['A time', 'A place name', 'An amount of money'],
          answer: 'A time',
          explanation: 'The sequence of questions about check-in followed by check-out clearly indicates a time.',
        },
        {
          text: 'Topic: "Student Health Services". Question: "The clinic is open from Monday to ___."',
          options: ['A day of the week', 'A number', 'A medical term'],
          answer: 'A day of the week',
          explanation: '"From Monday to ___" requires an ending day — e.g. "Friday" or "Saturday".',
        },
        {
          text: 'The form heading says "Insurance Claim Details". Question: "Policy number: ___"',
          options: ['A reference number or code', 'A name', 'A reason'],
          answer: 'A reference number or code',
          explanation: 'Policy numbers are alphanumeric codes. Listen carefully for letters and digits being spelled out.',
        },
      ],
    },
    {
      title: 'Complete the Predictions',
      type: 'fill-in',
      instructions: 'Fill in what type of answer you would predict for each gap.',
      questions: [
        {
          text: 'The lecture will focus on the effects of ___ on ocean temperatures. (Predict: a natural phenomenon or scientific topic, e.g. "climate change" or "pollution")',
          answer: 'noun',
          explanation: 'After "of" and before "on", a noun or noun phrase is expected — a scientific topic like "deforestation" or "carbon emissions".',
        },
        {
          text: 'Students should arrive ___ minutes before the exam starts. (Predict: a specific number)',
          answer: 'number',
          explanation: 'Before "minutes", a number is required — e.g. "fifteen" or "30".',
        },
        {
          text: 'The new dormitory is located near the ___ building. (Predict: a name or adjective)',
          answer: 'adjective',
          explanation: 'Before "building", expect a descriptive adjective or name — e.g. "science" or "main administration".',
        },
      ],
    },
    {
      title: 'Write Predictions for Sample Gaps',
      type: 'writing',
      instructions: 'Read each question and write out your full prediction — what type of answer you expect and why.',
      questions: [
        {
          text: 'Section heading: "Travel Arrangements". Question: "Departure airport: ___. Flight number: ___. Arrival time: ___." Write predictions for all three gaps.',
          example: 'Gap 1: I predict an airport name (e.g. "Heathrow" or "Gatwick") because the label says "airport". Gap 2: A flight code — a combination of letters and numbers (e.g. "BA 247"). I should listen for letters being spelled. Gap 3: A time (e.g. "3:45 pm") because "arrival time" clearly signals a clock time.',
          tip: 'Label each prediction with the gap number and explain your reasoning based on the question text and context.',
        },
        {
          text: 'You see this question: "The researcher found that ___ was the main cause of the decline in bee populations." What do you predict and what keywords will you listen for?',
          example: 'I predict a noun or noun phrase naming a cause — something like "pesticide use" or "habitat loss". Keywords to listen for: "cause", "reason", "because", "due to", "result of". I\'ll also listen for the researcher expressing a conclusion.',
          tip: 'Think about both the answer type AND the signpost language that might introduce the answer in the audio.',
        },
      ],
    },
  ],
  tips: [
    'Always use every second of preview time to read ahead and predict',
    'Write brief predictions in pencil next to each question before the audio starts',
    'Focus on predicting the answer type first, then the specific content',
    'If a prediction is wrong, don\'t cling to it — adapt as you listen',
  ],
  dailyPractice: [
    'Take any IELTS Listening question set and predict all answers before playing the audio',
    'Practise grammatical prediction: cover the answer in a completed exercise and predict the word class',
    'Read news headlines and predict what the article will discuss — this builds general prediction skills',
    'Time yourself: give yourself only 30 seconds to predict a set of 10 questions, as in the real exam',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Prediction Skills for IELTS Listening: teaches students to use question text, grammar cues, and surrounding context to anticipate answers before the audio plays. Covers answer type prediction, grammatical prediction (word class), and context-based prediction techniques.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...predictionSkillsData,
      title: predictionSkillsData.title + ' (Foundations)',
      overview: { ...predictionSkillsData.overview, duration: '15-20 minutes' },
      sections: predictionSkillsData.sections.slice(0, Math.max(2, Math.ceil(predictionSkillsData.sections.length / 2))),
      exercises: predictionSkillsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...predictionSkillsData,
      title: predictionSkillsData.title + ' (Advanced)',
      overview: { ...predictionSkillsData.overview, duration: '30-35 minutes' },
      sections: predictionSkillsData.sections,
      exercises: predictionSkillsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(predictionSkillsData.tips || []),
        'For Band 8+: Train yourself to predict not just answer types but specific vocabulary fields.',
        'Advanced tip: Practise predicting with Section 4 academic lectures where topics are less predictable.',
      ],
    };
  }
  return predictionSkillsData;
}
